import { FC } from 'react';

import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';

import { DisplayCurrency } from 'src/components';
import { Flex } from 'src/components/Flex';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useCurrency } from 'src/hooks/useCurrency';
import { isIreland } from 'src/lib/countryConfig';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import AccountDisplay from '../AccountDisplay';
import { CardSection } from '../CardSection';
import { RadioDisplay } from '../RadioDisplay';
import { TextDisplay } from '../TextDisplay';

export const BusinessInformation: FC = () => {
    const { merchantApplication } = useAddMerchantContext();
    const { currencySymbol } = useCurrency(merchantApplication?.partner?.countryDetails);

    const isSameAsChargeable =
        merchantApplication?.businessInformation?.settlementAccountSameAsChargeable ?? false;

    const settlementBankDetails = {
        sortCode: merchantApplication?.businessInformation?.settlementAccountSortCode ?? '',
        accountNumber:
            merchantApplication?.businessInformation?.settlementAccountAccountNumber ?? '',
        bankName: merchantApplication?.businessInformation?.settlementAccountBankName ?? '',
        branch: merchantApplication?.businessInformation?.settlementAccountBranch ?? '',
        addressLine1: merchantApplication?.businessInformation?.settlementAccountAddressLine1 ?? '',
        addressLine2: merchantApplication?.businessInformation?.settlementAccountAddressLine2 ?? '',
        town: merchantApplication?.businessInformation?.settlementAccountTown ?? '',
        postCode: merchantApplication?.businessInformation?.settlementAccountPostCode ?? '',
    };

    const chargeableBankDetails = {
        sortCode: merchantApplication?.businessInformation?.chargeableAccountSortCode ?? '',
        accountNumber:
            merchantApplication?.businessInformation?.chargeableAccountAccountNumber ?? '',
        bankName: merchantApplication?.businessInformation?.chargeableAccountBankName ?? '',
        branch: merchantApplication?.businessInformation?.chargeableAccountBranch ?? '',
        addressLine1: merchantApplication?.businessInformation?.chargeableAccountAddressLine1 ?? '',
        addressLine2: merchantApplication?.businessInformation?.chargeableAccountAddressLine2 ?? '',
        town: merchantApplication?.businessInformation?.chargeableAccountTown ?? '',
        postCode: merchantApplication?.businessInformation?.chargeableAccountPostCode ?? '',
    };

    return (
        <Flex flexDirection="column" gap="24px">
            <CardSection heading="Business Information">
                <Flex flexDirection="column" gap="24px">
                    <Flex justifyContent="space-between">
                        <TextDisplay
                            label="TA Code"
                            value={merchantApplication?.businessInformation?.taCode ?? ''}
                        />
                        <TextDisplay
                            label="Member of/TA Name"
                            value={merchantApplication?.businessInformation?.memberTaName ?? ''}
                        />
                    </Flex>
                    <RadioDisplay
                        label="Existing Acquirer Relationship"
                        value={
                            merchantApplication?.businessInformation
                                ?.existingAcquirerRelationship ?? false
                        }
                    />
                    <Flex justifyContent="space-between">
                        <TextDisplay
                            label="Merchant Number"
                            value={merchantApplication?.businessInformation?.merchantNumber ?? ''}
                        />
                        <TextDisplay
                            label="Membership Number"
                            value={merchantApplication?.businessInformation?.membershipNumber ?? ''}
                        />
                    </Flex>
                </Flex>
            </CardSection>
            <CardSection heading="Goods & Services">
                <Flex flexDirection="row" gap="50px">
                    <GoodsAndServicesRightSection merchantApplication={merchantApplication} />
                    <GoodsAndServicesLeftSection
                        merchantApplication={merchantApplication}
                        currencySymbol={currencySymbol}
                    />
                </Flex>
            </CardSection>
            <CardSection heading="Card Turnover">
                <Flex flexDirection="column" gap="24px">
                    <Flex justifyContent="space-between">
                        <Box flex={0.5} padding="8px">
                            <Typography
                                fontSize="14px"
                                color="#1D1D1B"
                                fontWeight="600"
                                lineHeight="21px"
                                marginBottom="24px"
                            >
                                Credit Card Turnover
                            </Typography>

                            <DisplayCurrency
                                centsAmount={
                                    merchantApplication?.businessInformation?.creditCardTurnover ||
                                    0
                                }
                                usersCurrency={currencySymbol}
                                style={{ fontSize: '20px', color: '#8E8E8D', fontWeight: 400 }}
                            />
                        </Box>
                        <Box flex={0.5} padding="8px">
                            <Typography
                                fontSize="14px"
                                color="#1D1D1B"
                                fontWeight="600"
                                lineHeight="21px"
                                marginBottom="24px"
                            >
                                Total Card Turnover
                            </Typography>

                            <DisplayCurrency
                                centsAmount={
                                    merchantApplication?.businessInformation?.totalCardTurnover || 0
                                }
                                usersCurrency={currencySymbol}
                                style={{ fontSize: '20px', color: '#8E8E8D', fontWeight: 400 }}
                            />
                        </Box>
                    </Flex>
                    <Flex justifyContent="space-between">
                        <Box flex={0.5} padding="8px">
                            <Typography
                                fontSize="14px"
                                color="#1D1D1B"
                                fontWeight="600"
                                lineHeight="21px"
                                marginBottom="24px"
                            >
                                Average Card Transaction Value
                            </Typography>

                            <DisplayCurrency
                                currencyAmount={
                                    merchantApplication?.businessInformation
                                        ?.averageCardTransaction || 0
                                }
                                usersCurrency={currencySymbol}
                                style={{ fontSize: '20px', color: '#8E8E8D', fontWeight: 400 }}
                            />
                        </Box>
                        <Box flex={0.5} padding="8px">
                            <Typography
                                fontSize="14px"
                                color="#1D1D1B"
                                fontWeight="600"
                                lineHeight="21px"
                                marginBottom="24px"
                            >
                                Total Annual Company Turnover
                            </Typography>

                            <DisplayCurrency
                                currencyAmount={
                                    merchantApplication?.businessInformation
                                        ?.totalAnnualCompanyTurnover || 0
                                }
                                usersCurrency={currencySymbol}
                                style={{ fontSize: '20px', color: '#8E8E8D', fontWeight: 400 }}
                            />
                        </Box>
                    </Flex>

                    <Box flex={0.5} padding="8px">
                        <Typography
                            fontSize="14px"
                            color="#1D1D1B"
                            fontWeight="600"
                            lineHeight="21px"
                            marginBottom="24px"
                        >
                            Mail/Telephone Order/ E-Commerce Sales(Card not present) as Percentage
                            of total card turnover
                        </Typography>
                        <DisplayCurrency
                            currencyAmount={
                                merchantApplication?.businessInformation
                                    ?.percentageOfCardTurnover || 0
                            }
                            usersCurrency={currencySymbol}
                            style={{ fontSize: '20px', color: '#8E8E8D', fontWeight: 400 }}
                        />
                    </Box>
                </Flex>
            </CardSection>

            <CardSection heading="Bank Details">
                <Flex flexDirection="column" gap="24px">
                    <RadioDisplay
                        label="Are Settlement Accounts (money paid in) the same as chargeable accounts?"
                        value={isSameAsChargeable}
                    />
                    <AccountDisplay
                        heading={
                            isSameAsChargeable
                                ? 'Settlement Account & Chargeable Account'
                                : 'Settlement Account'
                        }
                        bic={merchantApplication?.businessInformation?.settlementAccountBic ?? ''}
                        iban={merchantApplication?.businessInformation?.settlementAccountIban ?? ''}
                        accountName={
                            merchantApplication?.merchantInformation?.aboutBusiness
                                ?.legalCompanyName ?? ''
                        }
                        ukBankDetails={!isIreland && settlementBankDetails}
                    />
                    {!isSameAsChargeable && (
                        <AccountDisplay
                            heading="Chargeable Account"
                            bic={
                                merchantApplication?.businessInformation?.chargeableAccountBic ?? ''
                            }
                            iban={
                                merchantApplication?.businessInformation?.chargeableAccountIban ??
                                ''
                            }
                            accountName={
                                merchantApplication?.businessInformation
                                    ?.chargeableAccountAccountName ?? ''
                            }
                            ukBankDetails={!isIreland && chargeableBankDetails}
                        />
                    )}
                </Flex>
            </CardSection>
        </Flex>
    );
};

export const GoodsAndServicesRightSection = ({ merchantApplication }) => {
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Grid display="flex" flexDirection="column" justifyContent="space-between">
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Are there goods or services to be sold or supplied, owned or to be provided by a
                    third party?
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={merchantApplication?.businessInformation?.goodsServicesOwned}
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row" marginRight="7px">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled={isEditingDisabled}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled={isEditingDisabled}
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center" marginTop="10px">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Do you accept deposits prior to the supply of goods or services?
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={
                            merchantApplication?.businessInformation?.acceptDepositPayment
                        }
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row" marginRight="7px">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled={isEditingDisabled}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled={isEditingDisabled}
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center" marginTop="20px">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The size of the deposit paid in advance as % of total transaction value
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation?.sizeOfDepositTaken ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center" marginTop="28px">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The average time, in advance of delivery of goods/services of full payment, that
                    deposit is taken?
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem" marginBottom="20px">
                    {merchantApplication?.businessInformation?.averageTimeAdvancePaymentTaken ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Are stocks held at any address other than the trading address?
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={
                            merchantApplication?.businessInformation?.stocksHeldAnyAddress
                        }
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled={isEditingDisabled}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled={isEditingDisabled}
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center" marginTop="30px">
                <Typography style={{ fontSize: '14px' }}>
                    Do you levy a charge for memberships, subscriptions or insurance premiums
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={
                            merchantApplication?.businessInformation?.levyChargeForMembership
                        }
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row" marginBottom="20px">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled={isEditingDisabled}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled={isEditingDisabled}
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center" marginTop="20px">
                <Typography style={{ fontSize: '14px' }}>
                    The percentage of card turnover from memberships, subscriptions or insurance
                    premiums
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem" marginBottom="20px">
                    {merchantApplication?.businessInformation
                        ?.percentageOfCardTurnoverForMembership ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The average length of memberships, subscriptions or insurance premiums
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation?.averageLengthOfMembership ?? ''}
                </Typography>
            </Box>
        </Grid>
    );
};

export const GoodsAndServicesLeftSection = ({ merchantApplication, currencySymbol }) => {
    const isEditingDisabled = isApplicationEditable(merchantApplication);
    return (
        <Grid display="flex" flexDirection="column" justifyContent="space-between">
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Do you accept full payment prior to the supply of goods or services?
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={merchantApplication?.businessInformation?.acceptFullPayment}
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row" marginRight="7px">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled={isEditingDisabled}
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled={isEditingDisabled}
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box
                display="flex"
                flexDirection="row"
                gap="24px"
                alignItems="center"
                justifyContent="center"
            >
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The percentage of goods or services where payment is taken prior to delivery?
                </Typography>
                <Typography marginBottom="17px" color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation?.percentageDepositTaken ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The average time, in advance of delivery of goods/services, that full payment is
                    taken?
                </Typography>
                <Typography marginBottom="17px" color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation
                        ?.averageTimeFullPaymentDeliveryTaken ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Do you levy a charge for any guarantees or extended warranties?
                </Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        aria-labelledby="service-billed-radio-buttons"
                        name="service-billed-radio-group"
                        row
                        defaultValue={
                            merchantApplication?.businessInformation?.levyChargeForGuarantees
                        }
                        style={{ display: 'flex', flexDirection: 'row' }}
                    >
                        <Box display="flex" flexDirection="row">
                            <FormControlLabel
                                value
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="Yes"
                                disabled
                            />
                            <FormControlLabel
                                value={false}
                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                label="No"
                                disabled
                            />
                        </Box>
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box
                display="flex"
                flexDirection="row"
                gap="24px"
                alignItems="center"
                marginBottom="24px"
            >
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    The separate percentage of card turnover on guarantees/ warranties?
                </Typography>
                <TextDisplay
                    value={merchantApplication?.businessInformation?.percentageOfCardTurnover ?? ''}
                />
            </Box>
            <Box
                display="flex"
                flexDirection="row"
                gap="24px"
                alignItems="center"
                marginBottom="34px"
            >
                <Typography style={{ fontSize: '14px' }}>
                    The average length of guarantees/ warranties?
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation?.averageLengthOfGuarantees ?? ''}
                </Typography>
            </Box>
            <Box
                display="flex"
                flexDirection="row"
                gap="24px"
                alignItems="center"
                marginBottom="13px"
            >
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Percentage of goods returned whilst under guarantee/warranty
                </Typography>
                <Typography color="#8E8E8D" fontSize="1.5rem">
                    {merchantApplication?.businessInformation?.percentageOfGoodsReturned ?? ''}
                </Typography>
            </Box>
            <Box display="flex" flexDirection="row" gap="24px" alignItems="center">
                <Typography style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                    Cost of Membership
                </Typography>

                <DisplayCurrency
                    usersCurrency={currencySymbol}
                    currencyAmount={merchantApplication?.businessInformation?.costOfMembership || 0}
                />
            </Box>
        </Grid>
    );
};
